<template>
  <div ref="stage" class="reward-coin-shower" aria-live="polite" aria-atomic="true">
    <div class="reward-coin-shower__fx" aria-hidden="true">
      <img
        v-for="index in coinCount"
        :key="index"
        :src="resolvedCoin"
        alt=""
        class="reward-coin-shower__coin"
      />
    </div>

    <div ref="copy" class="reward-coin-shower__copy">
      <div class="reward-coin-shower__eyebrow">{{ eyebrow }}</div>
      <div class="reward-coin-shower__points flex gap-1 items-baseline justify-center">
        +{{ formattedPoints }}
        <img :src="resolvedCoin" alt="" class="size-6" />
      </div>
      <div class="reward-coin-shower__subtitle">{{ subtitle }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import gsap from 'gsap'
import defaultCoin from '@/../public/img/game/bolt.svg'

const props = withDefaults(defineProps<{
  points: number
  coinSrc?: string
  eyebrow?: string
  subtitle?: string
  coinCount?: number
  trigger?: string | number
  autoplay?: boolean
  fadeoutDelay?: number
}>(), {
  coinSrc: '',
  eyebrow: 'Boa!',
  subtitle: 'pontos conquistados',
  coinCount: 24,
  trigger: 0,
  autoplay: false,
  fadeoutDelay: 700,
})

const emit = defineEmits<{ start: []; complete: []; fadeoutComplete: [] }>()
const stage = ref<HTMLElement | null>(null)
const copy = ref<HTMLElement | null>(null)
const resolvedCoin = computed(() => props.coinSrc || defaultCoin)
const formattedPoints = computed(() => props.points.toLocaleString('pt-BR'))
let timeline: gsap.core.Timeline | null = null
let fadeout: gsap.core.Tween | null = null

async function play() {
  await nextTick()
  if (!stage.value || !copy.value) return

  timeline?.kill()
  fadeout?.kill()
  fadeout = null
  gsap.killTweensOf(stage.value.querySelectorAll('*'))
  emit('start')

  if (stage.value) {
    gsap.set(stage.value, { opacity: 1 })
  }

  const coins = Array.from(stage.value.querySelectorAll<HTMLElement>('.reward-coin-shower__coin'))
  gsap.set(copy.value, { opacity: 0, scale: 0.85, y: 12 })

  coins.forEach((coin) => {
    const size = gsap.utils.random(14, 36)
    gsap.set(coin, {
      width: size,
      height: size,
      left: `${gsap.utils.random(4, 96)}%`,
      top: gsap.utils.random(-110, -36),
      xPercent: -50,
      opacity: 1,
      rotation: gsap.utils.random(-180, 180),
      scale: gsap.utils.random(0.6, 1.2),
    })
  })

  timeline = gsap.timeline({
    onComplete: () => {
      emit('complete')
      if (props.fadeoutDelay > 0 && stage.value) {
        gsap.delayedCall(props.fadeoutDelay / 1000, () => {
          if (stage.value) {
            fadeout = gsap.to(stage.value, {
              opacity: 0,
              duration: 0.4,
              ease: 'power2.out',
              onComplete: () => emit('fadeoutComplete'),
            })
          } else {
            emit('fadeoutComplete')
          }
        })
      }
    },
  })
  timeline
    .to(copy.value, { opacity: 1, scale: 1.05, y: 0, duration: 0.35, ease: 'back.out(2.2)' }, 0.18)
    .to(copy.value, { scale: 1, duration: 0.16, ease: 'power2.out' }, 0.48)

  coins.forEach((coin, index) => {
    const delay = gsap.utils.random(0, 0.46) + index * 0.004
    timeline!
      .to(coin, {
        top: '112%',
        rotation: `+=${gsap.utils.random(220, 720)}`,
        opacity: 1,
        duration: gsap.utils.random(1.18, 1.86),
        ease: 'power1.in',
      }, delay)
      .to(coin, { opacity: 0, duration: 0.24, ease: 'power1.out' }, delay + 1.15)
  })

  timeline.to({}, { duration: 0.1 }, 1.95)
}

defineExpose({ play })
watch(() => props.trigger, (value, oldValue) => { if (value !== oldValue) void play() })
onMounted(() => { if (props.autoplay) void play() })
onBeforeUnmount(() => {
  timeline?.kill()
  fadeout?.kill()
  fadeout = null
  if (stage.value) gsap.killTweensOf(stage.value.querySelectorAll('*'))
})
</script>

<style scoped>
.reward-coin-shower {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: grid;
  place-items: center;
  overflow: hidden;
  isolation: isolate;
  contain: layout paint;
  pointer-events: none;
}
.reward-coin-shower__fx { position: absolute; inset: 0; z-index: 1; overflow: hidden; pointer-events: none; }
.reward-coin-shower__copy {
  position: relative;
  z-index: 2;
  max-width: calc(100% - 2rem);
  opacity: 0;
  border: 1px solid rgba(255, 255, 255, .75);
  border-radius: 1.5rem;
  padding: 1.25rem 2rem;
  text-align: center;
  background: rgba(255, 255, 255, .88);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 18px 44px rgba(49, 28, 74, .16);
  will-change: transform, opacity;
}
.reward-coin-shower__eyebrow { font-size: .75rem; line-height: 1rem; text-transform: uppercase; letter-spacing: .2em; font-weight: 900; color: #8b5cf6; }
.reward-coin-shower__points { font-size: clamp(2.25rem, 8vw, 3rem); line-height: 1; font-weight: 900; letter-spacing: -.025em; color: #2e1065; font-variant-numeric: tabular-nums; white-space: nowrap; }
.reward-coin-shower__subtitle { margin-top: .2rem; font-weight: 700; color: #64748b; }
.reward-coin-shower__coin {
  position: absolute;
  display: block;
  margin: 0;
  max-width: none;
  pointer-events: none;
  user-select: none;
  opacity: 0;
  will-change: transform, opacity, top;
}
</style>
